import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Input from "components/Input/Input";
import Button from "components/Button/Regular";
import ConfirmDialog from "components/ConfirmDialog";
import Select from "components/Input/Select";
import { ReactComponent as UserIcon } from "assets/icons/user.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import {
  CONTACT_TYPE,
  deleteOrgContact,
  readContact,
  updateOrgContact,
  uploadContactImage,
} from "services/contacts.service";
import vgToast from "utils/toast";

type FormValues = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
};

const titleFromApi = (t: string | undefined) => {
  if (!t) return "mr";
  const u = t.toUpperCase();
  if (u === "MRS") return "mrs";
  return "mr";
};

const titleToApi = (t: string) => {
  const m: Record<string, string> = { mr: "MR", mrs: "MRS" };
  return m[t?.toLowerCase?.()] ?? "MR";
};

const UpdateCustomerForm = () => {
  const navigate = useNavigate();
  const { id, orgId: orgFromRoute } = useParams();
  const { orgId: activeOrgId } = useActiveOrg();
  const { getAccessTokenSilently } = useAuth0();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [customerImageUrl, setCustomerImageUrl] = React.useState<string | null>(null);
  const [imageBroken, setImageBroken] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const orgId = orgFromRoute ? Number(orgFromRoute) : activeOrgId;
  const contactId = id ? Number(id) : NaN;

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      title: "mr",
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id || !Number.isFinite(contactId) || contactId <= 0 || orgId <= 0) {
        return;
      }
      setLoadError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await readContact(token, orgId, contactId);
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        if (!data) return;
        const url = data.cloudFileUrl?.trim();
        setCustomerImageUrl(url && url.length > 0 ? url : null);
        setImageBroken(false);
        reset({
          email: data.emailAddress,
          firstName: data.contactFirstName,
          lastName: data.contactLastName,
          title: titleFromApi(data.contactTitle),
        });
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, contactId, orgId, getAccessTokenSilently, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) {
      vgToast.error("Missing organisation or client id.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error } = await updateOrgContact(token, {
        orgId,
        contactId,
        contactFirstName: data.firstName,
        contactLastName: data.lastName,
        emailAddress: data.email,
        contactTitle: titleToApi(data.title),
        contactType: CONTACT_TYPE.CUSTOMER,
      });
      if (error) vgToast.error(error.message);
      else vgToast.success("Client saved.");
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageClick = () => {
    if (uploadingImage) return;
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) {
      vgToast.error("Save the client before uploading a photo.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input value so picking the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) {
      vgToast.error("Missing organisation or client id.");
      return;
    }
    setUploadingImage(true);
    // Optimistic preview while the upload is in flight.
    const previewUrl = URL.createObjectURL(file);
    const previousUrl = customerImageUrl;
    setImageBroken(false);
    setCustomerImageUrl(previewUrl);
    try {
      const token = await getAccessTokenSilently();
      const { data, error } = await uploadContactImage(token, orgId, contactId, file);
      if (error) {
        setCustomerImageUrl(previousUrl);
        vgToast.error(error.message);
        return;
      }
      if (data?.cloudFileUrl) {
        setCustomerImageUrl(data.cloudFileUrl);
      }
      vgToast.success("Photo uploaded.");
    } catch (err) {
      setCustomerImageUrl(previousUrl);
      vgToast.error((err as Error).message);
    } finally {
      URL.revokeObjectURL(previewUrl);
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) return;
    setDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error } = await deleteOrgContact(token, orgId, contactId);
      if (error) {
        vgToast.error(error.message);
        return;
      }
      setConfirmDeleteOpen(false);
      navigate("/app/clients");
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="px-10 pb-10">
      {loadError && (
        <div className="mb-6 text-sm text-red-500">{loadError}</div>
      )}

      <div className="flex flex-col items-start gap-6 pb-8 mb-8 border-b border-slate-200 sm:flex-row sm:items-center sm:gap-x-10">
        <button
          type="button"
          onClick={handleImageClick}
          disabled={uploadingImage}
          aria-label="Upload client photo"
          className="relative group shrink-0 h-44 w-44 rounded-full border border-vg-blue/20 bg-vg-blue/[0.04] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/40 focus-visible:ring-offset-2"
        >
          {customerImageUrl && !imageBroken ? (
            <img
              src={customerImageUrl}
              alt=""
              onError={() => setImageBroken(true)}
              className="h-full w-full object-cover transition group-hover:opacity-80"
            />
          ) : (
            <UserIcon className="absolute inset-0 m-auto h-20 w-20 text-vg-blue/40" />
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white text-xs font-semibold tracking-wide opacity-0 group-hover:opacity-100 transition pointer-events-none">
            {uploadingImage ? "UPLOADING…" : "CHANGE PHOTO"}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageChange}
        />

        {/* Single column on phones. The 12-col spans only apply from sm, otherwise
            Title/First/Last are forced onto one 375px row. */}
        <div className="grow grid grid-cols-1 gap-5 sm:grid-cols-12 sm:gap-x-6">
          <div className="sm:col-span-2">
            <Select
              {...register("title")}
              label="Title"
              options={[
                { title: "MR", value: "mr" },
                { title: "MRS", value: "mrs" },
              ]}
            />
          </div>

          <div className="sm:col-span-5">
            <Input
              {...register("firstName")}
              label="First Name"
              placeholder="Enter first name"
            />
          </div>

          <div className="sm:col-span-5">
            <Input
              {...register("lastName")}
              label="Last Name"
              placeholder="Enter last name"
            />
          </div>

          <div className="sm:col-span-12">
            <Input
              {...register("email")}
              label="Email Address"
              placeholder="Enter email address"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-3 flex-wrap">
        <Button
          color="Secondary"
          type="button"
          disabled={deleting || submitting}
          onClick={() => setConfirmDeleteOpen(true)}
        >
          <span>{deleting ? "Deleting…" : "Delete Client"}</span>
        </Button>
        <Button color="Primary" type="submit" disabled={submitting || deleting}>
          <span>{submitting ? "Saving…" : "Save Changes"}</span>
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete client?"
        description="This will permanently remove the client record. This action cannot be undone."
        confirmLabel="Delete client"
        busy={deleting}
        onCancel={() => {
          if (!deleting) setConfirmDeleteOpen(false);
        }}
        onConfirm={handleDelete}
      />
    </form>
  );
};

export default UpdateCustomerForm;
